import { onlineManager } from '@tanstack/react-query';
import AppEnvironment from '../../components/enums/appEnvironment.enum';
import { BrandIntegration } from '../context/ApplicationContext/applicationContext.types';
import { OfflineStatus, isOfflineStatus } from '../offline/domain/offlineOrder/offlineOrder.types';
import { UserRole } from '../users/roles';
import { Role } from './actions/types/actions.types';
import { OrderStatus } from './interfaces/order-status.interface';
import { GetOrderViewParams, StatusType, SynchronizedType } from './order.types';
import { OrderView } from './ordersView.types';

const validateByIntegrationTrigger = (
  orderStatus?: OrderStatus,
  integrations?: BrandIntegration[],
) => {
  const activeIntegration = integrations?.find((integration) => integration.active);

  const cannotEdit =
    orderStatus &&
    activeIntegration &&
    [StatusType.APPROVED, StatusType.SENT, activeIntegration.integrationTrigger].includes(
      orderStatus,
    );

  return cannotEdit;
};

const validateByOrderIntegrationStatus = (orderIntegrationStatus?: SynchronizedType) => {
  const cannotEdit =
    orderIntegrationStatus &&
    [
      SynchronizedType.PROCESSING,
      SynchronizedType.PROCESSING_AFTER_RESET,
      SynchronizedType.SUCCESS,
      SynchronizedType.SUCCESS_WITH_PAYMENT_ERROR,
    ].includes(orderIntegrationStatus);
  return cannotEdit;
};

const userCanEditOrder = (role: UserRole, orderStatus: StatusType) => {
  switch (role) {
    case Role.COMPRADOR:
    case Role.ADMIN_LOJA:
      return [StatusType.DRAFT].includes(orderStatus);
    case Role.VENDEDOR:
      return [StatusType.DRAFT, StatusType.IN_REVISION].includes(orderStatus);
    case Role.ADMIN_MARCA:
      return [StatusType.DRAFT, StatusType.IN_REVISION, StatusType.ON_APPROVAL].includes(
        orderStatus,
      );
    default:
      return false;
  }
};

export const getOrderView = ({
  role,
  orderStatus,
  integrations,
  canEditOrder,
  orderIntegrationStatus,
  appEnvironment = AppEnvironment.BRAND,
}: GetOrderViewParams): OrderView => {
  if (appEnvironment === AppEnvironment.STORE) {
    return OrderView.FINISHED;
  }

  const isOfflineEditable =
    !onlineManager.isOnline() &&
    isOfflineStatus(orderStatus) &&
    [OfflineStatus.OFFLINE, OfflineStatus.ERROR_SYNCING].includes(orderStatus);

  if (isOfflineEditable) {
    return OrderView.EDIT;
  }

  const integrationTriggerNeedsFinishedView =
    !isOfflineStatus(orderStatus) && validateByIntegrationTrigger(orderStatus, integrations);

  const orderIntegrationStatusNeedsFinishedView =
    validateByOrderIntegrationStatus(orderIntegrationStatus);

  if (integrationTriggerNeedsFinishedView || orderIntegrationStatusNeedsFinishedView) {
    return OrderView.FINISHED;
  }

  if (
    userCanEditOrder(role as UserRole, orderStatus as StatusType) ||
    (orderStatus === StatusType.SUGGESTED && canEditOrder)
  ) {
    return OrderView.EDIT;
  }

  if (orderStatus === StatusType.SUGGESTED) {
    return OrderView.SUGGESTION;
  }

  return OrderView.FINISHED;
};
